import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Paper, Snackbar, Alert, Typography,
} from '@mui/material';
import AppShell from '../components/Layout/AppShell';
import HomeMenuButton from '../components/Home/HomeMenuButton';
import CalendarioCard from '../components/Home/CalendarioCard';
import MuralAvisosCard from '../components/Home/MuralAvisosCard';
import { SearchField } from '../components/UI';
import { MENU_ITEMS } from '../mocks/menu.mock';
import { MENU_HOME_PERMISSIONS, NO_PERMISSION_AVAILABLE } from '../mocks/menuHome.permissions';
import { MENU_HOME_ROUTES } from '../mocks/menuHome.routes';
import { hasUserPermissions } from '../../helpers/utils/permissoes';
import * as C from '../theme/colors.constants';
import { rota } from '../prototipo.config';
import { changeKeyHandler, KEY_NUMBER } from '../../helpers/utils/changeEvents.utils';
import { RESULT_NOT_FOUND } from '../../helpers/constants/global.constants';
import useBuscarProcessoPorNumero from '../hooks/useBuscarProcessoPorNumero';

/**
 * Grid: 2 colunas de botões à esquerda (~65% da largura) + 1 coluna de
 * cards à direita (~35%), como no print. Em telas maiores as três colunas
 * crescem proporcionalmente porque a medida é `fr`, não px.
 *
 * RBAC dos botões é apenas cosmético (esconder/desabilitar não substitui a
 * validação de servidor) — cada endpoint real continua validando a permissão
 * por si só. Ver `src/prototipo/mocks/menuHome.permissions.js`.
 */
export default function HomePage() {
  const history = useHistory();
  const { buscar, buscando: buscandoProcesso } = useBuscarProcessoPorNumero();

  const [nroProcesso, setNroProcesso] = useState('');
  const [toast, setToast] = useState(null);

  const menuItemsComRbac = useMemo(() => MENU_ITEMS.map((item) => {
    // Item sem entrada no catálogo falha fechado (nunca visível/habilitado),
    // nunca aberto — um id novo em MENU_ITEMS sem entrada correspondente aqui
    // não deve virar "sem gate" por acidente.
    const permissao = MENU_HOME_PERMISSIONS[item.id]
      || { gate: NO_PERMISSION_AVAILABLE, viewPermissions: null };

    const views = item.views && permissao.viewPermissions
      ? item.views.filter((view) => {
        const codigosView = permissao.viewPermissions[view];
        return !codigosView || hasUserPermissions(...codigosView);
      })
      : item.views;

    // `permissao.gate` truthy cobre tanto um array de códigos reais quanto o
    // sentinela NO_PERMISSION_AVAILABLE (array vazio, também truthy em JS) —
    // hasUserPermissions() sem argumentos já retorna false, então o mesmo
    // `!hasUserPermissions(...gate)` resolve os dois casos como desabilitado.
    const disabled = permissao.gate
      ? !hasUserPermissions(...permissao.gate)
      : item.kind === 'dropdown' && (views?.length ?? 0) === 0;

    return { ...item, views, disabled };
  }), []);

  const abrirDestino = (itemId, label) => {
    if (itemId === 'localizar') {
      history.push(rota('/pesquisa'));
      return;
    }

    const rotasDoItem = MENU_HOME_ROUTES[itemId];
    const path = typeof rotasDoItem === 'string' ? rotasDoItem : rotasDoItem?.[label];
    if (path) {
      history.push(path);
      return;
    }

    // Item/view sem rota real mapeada ainda (ver menuHome.routes.js) — sinaliza
    // a navegação em vez de abrir tela.
    setToast(`Abriria: ${label}`);
  };

  const changeNroProcesso = (e) => {
    changeKeyHandler(e, KEY_NUMBER);
    setNroProcesso(e.target.value);
  };

  const buscarProcesso = () => buscar(nroProcesso, {
    // history.push direto é seguro aqui porque esta página não tem nenhum
    // efeito próprio observando state.pesquisaProcessosUnificada (confirmado
    // por grep). Se um efeito for adicionado no futuro (ex.: ao adotar
    // useDispararPesquisaAtalho, como em Topbar.jsx/PesquisaProcessosPage.jsx),
    // guardar com um ref por "quem disparou" (ver disparadoPelaPaginaRef em
    // PesquisaProcessosPage.jsx) para evitar navegação duplicada.
    onEncontrado: () => history.push('/prototipo/processos'),
    onNaoEncontrado: () => setToast(RESULT_NOT_FOUND),
  });

  const renderItem = (item) => {
    if (item.kind === 'search') {
      return (
        <Paper
          key={item.id}
          sx={{
            height: 'clamp(56px, 4.1vw, 92px)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 'clamp(12px, 0.9vw, 24px)',
            boxShadow: C.shadowCard,
          }}
        >
          <Typography sx={{ color: C.warmGrey, fontSize: 'clamp(13px, 0.78vw, 18px)', flexShrink: 0 }}>
            {item.label}
          </Typography>
          <SearchField
            value={nroProcesso}
            onChange={changeNroProcesso}
            onSubmit={buscarProcesso}
            disabled={buscandoProcesso}
            loading={buscandoProcesso}
            sx={{
              '& .MuiOutlinedInput-root': { backgroundColor: 'transparent' },
              '& fieldset': { border: 'none' },
            }}
          />
        </Paper>
      );
    }

    return (
      <HomeMenuButton
        key={item.id}
        label={item.label}
        views={item.views}
        showDropdown={item.kind === 'dropdown'}
        disabled={item.disabled}
        onSelect={texto => abrirDestino(item.id, texto)}
      />
    );
  };

  return (
    <AppShell>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.92fr)' },
          gap: 'clamp(12px, 1.05vw, 28px)',
          alignItems: 'start',
        }}
      >
        {/* Coluna 1 de botões */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 1.05vw, 28px)' }}>
          {menuItemsComRbac.filter(i => i.col === 1).map(renderItem)}
        </Box>

        {/* Coluna 2 de botões */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 1.05vw, 28px)' }}>
          {menuItemsComRbac.filter(i => i.col === 2).map(renderItem)}
        </Box>

        {/* Coluna 3: calendário + mural */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(12px, 1.05vw, 28px)',
            gridColumn: { xs: '1', md: '1 / -1', lg: 'auto' },
          }}
        >
          <CalendarioCard />
          <MuralAvisosCard />
        </Box>
      </Box>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2200}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" sx={{ backgroundColor: C.navy }}>
          {toast}
        </Alert>
      </Snackbar>
    </AppShell>
  );
}
